import { toPng, getFontEmbedCSS } from "html-to-image";
import { jsPDF } from "jspdf";
import QRCode from "qrcode";
import JSZip from "jszip";

// A card is printed at 100mm x 50mm; at 96dpi that is 378 x 189 CSS pixels.
const W = 378;
const H = 189;

const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

const row = (label, value, ltr, size = 12) =>
  `<div style="display:flex;gap:6px;align-items:baseline;line-height:1.35">
     <span style="color:#57534E;font-size:10px;white-space:nowrap">${esc(label)}:</span>
     <span style="font-weight:700;font-size:${size}px;color:#1C1917;overflow:hidden;text-overflow:ellipsis;white-space:nowrap" ${ltr ? 'dir="ltr"' : ""}>${esc(value)}</span>
   </div>`;

const cardHtml = (card, meta, tt, dir, qr) => `
  <div dir="${dir}" style="width:${W}px;height:${H}px;box-sizing:border-box;background:#FAF7F2;border:2px solid #8B3A2B;border-radius:10px;
       font-family:Cairo,sans-serif;padding:10px 14px;display:flex;flex-direction:column;gap:4px;overflow:hidden">
    <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;border-bottom:1px solid #E5DCCB;padding-bottom:4px">
      <span style="font-weight:800;font-size:13px;color:#8B3A2B">${esc(tt.cardTitle)}</span>
      <img src="${meta.logo}" style="height:30px;width:auto" />
    </div>
    <div style="display:flex;gap:10px;align-items:stretch;flex:1;min-height:0">
      <div style="flex:1;min-width:0;display:flex;flex-direction:column;gap:3px">
        ${row(tt.cardStudent, card.name)}${row(tt.cardClass, meta.className)}
        ${meta.teacherName ? row(tt.cardTeacher, meta.teacherName) : ""}
        <div style="background:#fff;border:1px dashed #B45309;border-radius:8px;padding:3px 8px">
          ${row(tt.cardUsername, card.username, true, 15)}${row(tt.cardPassword, card.password, true, 15)}
        </div>
        ${row(tt.cardSite, meta.site, true, 10)}
      </div>
      <div style="width:84px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:2px">
        <img src="${qr}" style="width:80px;height:80px;display:block" />
        <span style="font-size:9px;color:#57534E;text-align:center;line-height:1.2">${esc(tt.cardScan)}</span>
      </div>
    </div>
  </div>`;

const sectionHtml = (s, tt, dir, logo) => `
  <div dir="${dir}" style="width:${W}px;height:${H}px;box-sizing:border-box;background:#8B3A2B;color:#FAF7F2;border-radius:10px;
       font-family:Cairo,sans-serif;padding:14px 18px;display:flex;flex-direction:column;justify-content:center;gap:6px;overflow:hidden">
    <img src="${logo}" style="height:34px;width:auto;align-self:flex-start;background:#FAF7F2;border-radius:6px;padding:2px 6px" />
    <span style="font-size:11px;opacity:.85">${esc(tt.cardClass)}</span>
    <span style="font-weight:800;font-size:22px;line-height:1.2;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${esc(s.class_name)}</span>
    <span style="font-size:12px">${s.teacher_name ? `${esc(tt.cardTeacher)}: <b>${esc(s.teacher_name)}</b> · ` : ""}${esc(tt.cardCount(s.cards.length))}</span>
  </div>`;

const qrFor = (username) =>
  QRCode.toDataURL(`${window.location.origin}/login?u=${encodeURIComponent(username)}`, { margin: 0, width: 240, color: { dark: "#1C1917", light: "#FAF7F2" } });

const LOGO = () => `${window.location.origin}/images/yallahero-logo-v2.webp`;
const CARD_SITE = "Yallahero.com";

// Turns a list of sections into card HTML; each section can start with a divider card.
const buildHtml = async (sections, tt, dir, withDividers) => {
  const out = [];
  for (const s of sections) {
    const cards = Array.isArray(s?.cards) ? s.cards : [];
    if (withDividers) out.push(sectionHtml({ ...s, cards }, tt, dir, LOGO()));
    const meta = { className: s?.class_name ?? "", teacherName: s?.teacher_name ?? "", site: CARD_SITE, logo: LOGO() };
    for (const c of cards) out.push(cardHtml(c, meta, tt, dir, await qrFor(c.username)));
  }
  return out;
};

const renderPngs = async (htmls) => {
  const host = document.createElement("div");
  host.style.cssText = "position:fixed;left:-10000px;top:0;";
  document.body.appendChild(host);
  try {
    const fontEmbedCSS = await getFontEmbedCSS(document.body).catch(() => "");
    const out = [];
    for (const html of htmls) {
      host.innerHTML = html;
      const node = host.firstElementChild;
      await Promise.all([...node.querySelectorAll("img")].map((img) => (img.complete ? null : new Promise((r) => { img.onload = r; img.onerror = r; }))));
      out.push(await toPng(node, { pixelRatio: 3, fontEmbedCSS, width: W, height: H }));
    }
    return out;
  } finally {
    host.remove();
  }
};

const safeName = (s) => (s || "class").replace(/[\\/:*?"<>|]/g, "-");

const buildPdf = (pngs) => {
  const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: [100, 50] });
  pngs.forEach((p, i) => {
    if (i) pdf.addPage([100, 50], "landscape");
    pdf.addImage(p, "PNG", 0, 0, 100, 50);
  });
  return pdf;
};

const savePdf = (pngs, fileName) => buildPdf(pngs).save(`${safeName(fileName)}-cards.pdf`);

// One PDF per class section, bundled into a single zip (e.g. a whole school for its teachers).
export const downloadSectionsZip = async (data, tt, dir, fileName) => {
  const sections = Array.isArray(data?.sections) ? data.sections.filter((s) => s?.cards?.length) : [];
  const zip = new JSZip();
  const used = new Set();
  for (const s of sections) {
    const pdf = buildPdf(await renderPngs(await buildHtml([s], tt, dir, false)));
    let name = safeName(s.class_name);
    for (let n = 2; used.has(name); n++) name = `${safeName(s.class_name)} (${n})`;
    used.add(name);
    zip.file(`${name}.pdf`, pdf.output("arraybuffer"));
  }
  const blob = await zip.generateAsync({ type: "blob" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${safeName(fileName)}-cards.zip`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 10000);
};

export const downloadCardsPdf = async (data, tt, dir, fileName) => {
  savePdf(await renderPngs(await buildHtml([data], tt, dir, false)), fileName || data?.class_name);
};

export const downloadSectionsPdf = async (data, tt, dir, fileName) => {
  const sections = Array.isArray(data?.sections) ? data.sections.filter((s) => s?.cards?.length) : [];
  savePdf(await renderPngs(await buildHtml(sections, tt, dir, true)), fileName);
};

export const printCards = async (data, tt, dir) => {
  const pngs = await renderPngs(await buildHtml([data], tt, dir, false));
  const frame = document.createElement("iframe");
  frame.style.cssText = "position:fixed;width:0;height:0;border:0;";
  document.body.appendChild(frame);
  const doc = frame.contentDocument;
  doc.open();
  doc.write(`<html><head><style>@page{size:100mm 50mm;margin:0}body{margin:0}img{display:block;width:100mm;height:50mm;page-break-after:always}</style></head>
    <body>${pngs.map((p) => `<img src="${p}" />`).join("")}</body></html>`);
  doc.close();
  await new Promise((r) => setTimeout(r, 300));
  frame.contentWindow.focus();
  frame.contentWindow.print();
  setTimeout(() => frame.remove(), 60000);
};
